import { useCallback, useEffect, useMemo, useRef } from 'react';

import Editor from '@monaco-editor/react';
import { Box, useTheme } from '@mui/material';

const DEBOUNCE_CONTENT_ANALYSIS_MS = 150;

interface YamlEditorProps {
  value: string;
  onChange?: (value: string) => void;
  isReadOnly?: boolean;
  showMinimap?: boolean;
  height?: string | number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Monaco editor type
  onEditorMount?: (editor: any) => void;
  onCanUndoChange?: (canUndo: boolean) => void;
  onCanRedoChange?: (canRedo: boolean) => void;
  onShowSchemaButtonChange?: (show: boolean) => void;
  onSqlQueryChange?: (query: string | null) => void;
}

/**
 * YAML/JSON Editor component using Monaco Editor (fully uncontrolled).
 *
 * ARCHITECTURE — Why there is NO sync effect on `value`:
 *
 *   The classic controlled-editor approach (value prop → useEffect → editor.setValue)
 *   is fundamentally broken with fast typing because useEffect runs AFTER paint.
 *   When the user types faster than React can render, the effect fires with a stale
 *   `value` and calls editor.setValue(staleValue), resetting the cursor to 1:1.
 *
 *   Instead, this component is fully uncontrolled:
 *   - `defaultValue` sets the initial content on mount
 *   - User typing goes Monaco → onChange → parent (context state for save)
 *   - External changes (recipe load, version switch, discard, save response)
 *     are pushed IMPERATIVELY by the parent via the editor ref it obtains
 *     from `onEditorMount`. The parent calls `editorRef.current.setValue()`
 *     directly, bypassing the React render cycle entirely.
 *
 *   This eliminates the race condition completely.
 */
export const YamlEditor = ({
  value,
  onChange,
  isReadOnly = false,
  showMinimap = true,
  height: _height = '100%',
  onEditorMount,
  onCanUndoChange,
  onCanRedoChange,
  onShowSchemaButtonChange,
  onSqlQueryChange,
}: YamlEditorProps) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Monaco editor ref type
  const editorRef = useRef<any>(null);

  // Store callback refs so Monaco event handlers (registered once at mount)
  // always call the current callbacks without needing to re-register.
  const onCanUndoChangeRef = useRef(onCanUndoChange);
  onCanUndoChangeRef.current = onCanUndoChange;
  const onCanRedoChangeRef = useRef(onCanRedoChange);
  onCanRedoChangeRef.current = onCanRedoChange;
  const onShowSchemaButtonChangeRef = useRef(onShowSchemaButtonChange);
  onShowSchemaButtonChangeRef.current = onShowSchemaButtonChange;
  const onSqlQueryChangeRef = useRef(onSqlQueryChange);
  onSqlQueryChangeRef.current = onSqlQueryChange;

  // Debounce timers for content analysis
  const contentAnalysisTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cursorAnalysisTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Track last dispatched values to avoid duplicate callbacks
  const lastCanUndoRef = useRef(false);
  const lastCanRedoRef = useRef(false);
  const lastHasSchemasRef = useRef(false);
  const lastSqlQueryRef = useRef<string | null>(null);

  const handleEditorDidMount = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Monaco types
    (editor: any, _monaco: any) => {
      editorRef.current = editor;

      // ---- Content change handler (debounced analysis) ----
      editor.onDidChangeModelContent(() => {
        if (contentAnalysisTimerRef.current) {
          clearTimeout(contentAnalysisTimerRef.current);
        }

        contentAnalysisTimerRef.current = setTimeout(() => {
          const model = editor.getModel();
          if (!model) return;

          // Undo / Redo state – only callback when changed
          const canUndo = model.canUndo();
          const canRedo = model.canRedo();
          if (canUndo !== lastCanUndoRef.current) {
            lastCanUndoRef.current = canUndo;
            onCanUndoChangeRef.current?.(canUndo);
          }
          if (canRedo !== lastCanRedoRef.current) {
            lastCanRedoRef.current = canRedo;
            onCanRedoChangeRef.current?.(canRedo);
          }

          // Schema detection
          const content = model.getValue();
          const hasSchemas = /schema:|schemas:/i.test(content);
          if (hasSchemas !== lastHasSchemasRef.current) {
            lastHasSchemasRef.current = hasSchemas;
            onShowSchemaButtonChangeRef.current?.(hasSchemas);
          }
        }, DEBOUNCE_CONTENT_ANALYSIS_MS);
      });

      // ---- Cursor position change handler (debounced SQL detection) ----
      editor.onDidChangeCursorPosition(() => {
        if (cursorAnalysisTimerRef.current) {
          clearTimeout(cursorAnalysisTimerRef.current);
        }

        cursorAnalysisTimerRef.current = setTimeout(() => {
          const model = editor.getModel();
          const position = editor.getPosition();
          if (!model || !position) return;

          const lineContent = model.getLineContent(position.lineNumber);
          let sqlQuery: string | null = null;

          if (/\b(SELECT|UPDATE|INSERT|DELETE|WITH)\b/i.test(lineContent)) {
            sqlQuery = lineContent.trim();

            // Expand to multi-line SQL (basic detection)
            let startLine = position.lineNumber;
            let endLine = position.lineNumber;

            while (startLine > 1) {
              const prevLine = model.getLineContent(startLine - 1);
              if (prevLine.trim() && !prevLine.trim().endsWith(':')) {
                sqlQuery = `${prevLine.trim()} ${sqlQuery}`;
                startLine--;
              } else {
                break;
              }
            }

            while (endLine < model.getLineCount()) {
              const nextLine = model.getLineContent(endLine + 1);
              if (nextLine.trim() && !/^[a-zA-Z_]+:/.test(nextLine.trim())) {
                sqlQuery = `${sqlQuery} ${nextLine.trim()}`;
                endLine++;
              } else {
                break;
              }
            }
          }

          if (sqlQuery !== lastSqlQueryRef.current) {
            lastSqlQueryRef.current = sqlQuery;
            onSqlQueryChangeRef.current?.(sqlQuery);
          }
        }, DEBOUNCE_CONTENT_ANALYSIS_MS);
      });

      // Initial undo/redo state
      const model = editor.getModel();
      if (model) {
        lastCanUndoRef.current = model.canUndo();
        lastCanRedoRef.current = model.canRedo();
        onCanUndoChangeRef.current?.(lastCanUndoRef.current);
        onCanRedoChangeRef.current?.(lastCanRedoRef.current);
      }

      // Call parent callback — the parent stores this ref and uses it for
      // imperative external pushes (recipe load, version switch, discard).
      if (onEditorMount) {
        onEditorMount(editor);
      }
    },
    [onEditorMount]
  );

  const handleEditorChange = useCallback(
    (newValue: string | undefined) => {
      onChange?.(newValue || '');
    },
    [onChange]
  );

  // Update minimap when prop changes
  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.updateOptions({
        minimap: { enabled: showMinimap },
      });
    }
  }, [showMinimap]);

  // Cleanup debounce timers
  useEffect(() => {
    return () => {
      if (contentAnalysisTimerRef.current) clearTimeout(contentAnalysisTimerRef.current);
      if (cursorAnalysisTimerRef.current) clearTimeout(cursorAnalysisTimerRef.current);
    };
  }, []);

  // Memoize options to prevent @monaco-editor/react from calling
  // editor.updateOptions() on every parent re-render.
  const editorOptions = useMemo(
    () => ({
      readOnly: isReadOnly,
      minimap: { enabled: showMinimap },
      scrollBeyondLastLine: false,
      fontSize: 13,
      lineNumbers: 'on' as const,
      renderWhitespace: 'selection' as const,
      folding: true,
      automaticLayout: true,
      wordWrap: 'on' as const,
      tabSize: 2,
    }),
    [isReadOnly, showMinimap]
  );

  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: isDarkMode ? '#1e1e1e' : '#ffffff',
        overflow: 'hidden',
        minHeight: 0,
        minWidth: 0,
      }}
    >
      <Editor
        height="100%"
        width="100%"
        defaultLanguage="yaml"
        defaultValue={value}
        onChange={handleEditorChange}
        onMount={handleEditorDidMount}
        theme={isDarkMode ? 'vs-dark' : 'vs'}
        options={editorOptions}
      />
    </Box>
  );
};
